/**
 * Wallet Summary Context
 *
 * Provides real-time access to the user's wallet summary (balance, totals)
 * from Firestore at users/{uid}/wallet/summary.
 *
 * Balance updates automatically when transactions are created via the
 * atomic functions in walletRepository.ts.
 *
 * ACCOUNT WALLET DISPLAY (owner decision 2026-07-13: "display"): while the
 * signed-in user is ACTING IN AN ACCOUNT whose paying wallet is not their own
 * (e.g. a member inside an owner-personal or workspace-wallet account), the
 * exposed `summary` carries the ACCOUNT's paying-wallet balance from the
 * server-written mirror at accounts/{accountId}/wallet/displaySummary — so
 * every display surface (wallet page, low-balance gate/banner/popup) shows the
 * number that actually moves when they chat. This is DISPLAY-ONLY: money
 * routing is untouched, and money-movement consumers must use the always-
 * personal `personalSummary` also exposed here. Resolution logic is pure —
 * see walletDisplayResolver.ts.
 */

import type { PlanAllocationView } from '../config/planAllocation';
import type { BillingPauseView } from '../config/billingPause';
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Unsubscribe } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useAuth } from './AuthContext';
import { WalletSummary, createDefaultWalletSummary } from '../types/walletFirestore';
import {
  onWalletSummarySnapshot,
  getWalletSummary,
  onAccountWalletDisplaySnapshot,
  onAccountMembershipAccountIdSnapshot,
} from '../repositories/walletRepository';
import { useMaybeOLVRate } from './OLVRateContext';
import {
  resolveDisplayedWallet,
  AccountMirrorInput,
  AccountMirrorStatus,
  WalletDisplayScope,
} from './walletDisplayResolver';

/** Fallback rate when OLV rate context is unavailable */
const FALLBACK_RATE_USD = 0.001;

// ============================================================================
// Types
// ============================================================================

interface WalletSummaryState {
  /** Current wallet summary (balance, totals) */
  summary: WalletSummary;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /**
   * The uid the committed `summary` actually belongs to (null = the signed-out
   * default). FALSE-LOW-BALANCE FIX (Miles 2026-07-12): on SPA sign-in,
   * `hasUser` flips true in the SAME commit (derived from confirmedUid) while
   * `isLoading: true` only lands in a post-commit effect — one committed
   * render read as "signed in, settled, $0" and injected the '⚠ balance too
   * low' banner into a fresh thread. Tracking the summary's owner lets the
   * exposed isLoading cover that window declaratively.
   */
  summaryForUid: string | null;
}

/**
 * ACCOUNT WALLET DISPLAY (owner decision 2026-07-13: "display"): reactive state
 * for the acting-account context + the server-written display mirror at
 * accounts/{accountId}/wallet/displaySummary. Resolution is pure — see
 * walletDisplayResolver.ts — and fail-safe to the personal wallet.
 */
interface AccountDisplayState {
  /** Whether the account_membership back-pointer listener has fired once. */
  membershipLoaded: boolean;
  /** Acting account id from users/{uid}/account_membership/main (null = none). */
  accountId: string | null;
  /** Mirror listener lifecycle. */
  mirrorStatus: AccountMirrorStatus;
  /** Converted mirror summary (only when mirrorStatus === 'ready'). */
  mirrorSummary: WalletSummary | null;
  /** payer_uid stamped by the server (owner-personal payer, null = workspace pool). */
  mirrorPayerUid: string | null;
  /** The acting account's paid-plan allocation (2026-10-11), from the same mirror; null when it has none. */
  mirrorPlanAllocation: PlanAllocationView | null;
  /** The acting account's billing pause (2026-10-11, no grace), from the same mirror; null when none. */
  mirrorBillingPause: BillingPauseView | null;
}

const DEFAULT_ACCOUNT_DISPLAY_STATE: AccountDisplayState = {
  membershipLoaded: false,
  accountId: null,
  mirrorStatus: 'idle',
  mirrorSummary: null,
  mirrorPayerUid: null,
  mirrorPlanAllocation: null,
  mirrorBillingPause: null,
};

interface WalletSummaryContextValue extends WalletSummaryState {
  /** Refresh the wallet summary from Firestore */
  refresh: () => Promise<void>;
  /**
   * Whether a real (non-anonymous) Firebase-Auth-confirmed user is signed in,
   * sourced from onAuthStateChanged (authoritative) — NOT the stale localStorage
   * AuthContext. False for signed-out visitors (including ones who were signed in
   * earlier this session). Consumers use this to suppress balance warnings that
   * would otherwise fire on the default $0 summary shown while signed out.
   */
  hasUser: boolean;
  /**
   * ACCOUNT WALLET DISPLAY: which wallet `summary` belongs to. 'account' means
   * the user is acting in an account whose PAYING wallet is not their own, and
   * `summary` carries the account (owner/workspace) balance from the display
   * mirror. 'personal' is byte-for-byte the historical behavior.
   */
  displayScope: WalletDisplayScope;
  /** The acting account id when displayScope === 'account' (for labeling), else null. */
  displayAccountId: string | null;
  /**
   * The signed-in user's OWN wallet summary, regardless of display scope.
   * MONEY-MOVEMENT consumers (top-up success detection, deposit/withdraw caps)
   * MUST use this — the money-routing itself is unchanged by the display
   * decision, so their validations must track the wallet that actually moves.
   */
  personalSummary: WalletSummary;
  /** Loading state of the personal summary (W-round summaryForUid semantics). */
  personalIsLoading: boolean;
  /**
   * The acting account's paid-plan monthly allocation (2026-10-11): used
   * before the wallet, so a paying account with plan pr0 left is never "out
   * of balance" at a $0 wallet. Null when the account has no paid plan.
   */
  planAllocation: PlanAllocationView | null;
  /**
   * The acting account's paid plan, paused for a failed payment (2026-10-11,
   * no grace): new work waits until a payment succeeds. Null when not known.
   */
  billingPause: BillingPauseView | null;
}

// ============================================================================
// Context
// ============================================================================

const WalletSummaryContext = createContext<WalletSummaryContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function WalletSummaryProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const olvRateContext = useMaybeOLVRate();
  const [state, setState] = useState<WalletSummaryState>({
    summary: createDefaultWalletSummary(),
    isLoading: true,
    error: null,
    summaryForUid: null,
  });
  // Store raw OLV balance from Firestore (USD is derived, not stored)
  const rawOlvBalanceRef = useRef<number>(0);
  const unsubRef = useRef<Unsubscribe | null>(null);
  // Refs to hold latest callback versions (avoids stale closure in snapshot listener)
  const deriveUsdBalanceRef = useRef<(olvBalance: number) => number>(() => 0);
  const getEffectiveRateRef = useRef<() => number>(() => FALLBACK_RATE_USD);

  // Track the Firebase Auth-confirmed UID. Using onAuthStateChanged as the
  // single source of truth guarantees Firestore has valid auth credentials
  // before we start the listener (eliminates the localStorage / AuthContext race).
  const [confirmedUid, setConfirmedUid] = useState<string | null>(null);

  // ACCOUNT WALLET DISPLAY (2026-07-13): acting-account context + display mirror.
  const [accountDisplay, setAccountDisplay] = useState<AccountDisplayState>(
    DEFAULT_ACCOUNT_DISPLAY_STATE
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (fbUser) => {
      const uid = fbUser && !fbUser.isAnonymous ? fbUser.uid : null;
      console.log('[WalletSummaryContext] onAuthStateChanged — confirmedUid:', uid);
      setConfirmedUid(uid);
    });
    return unsub;
  }, []);

  // Helper to get the effective exchange rate
  const getEffectiveRate = useCallback((): number => {
    return olvRateContext?.getEffectiveRate() ?? FALLBACK_RATE_USD;
  }, [olvRateContext]);

  // Helper to derive USD from OLV balance
  const deriveUsdBalance = useCallback((olvBalance: number): number => {
    const rate = getEffectiveRate();
    return olvBalance * rate;
  }, [getEffectiveRate]);

  // Keep refs updated so snapshot callback always uses latest functions
  useEffect(() => {
    deriveUsdBalanceRef.current = deriveUsdBalance;
    getEffectiveRateRef.current = getEffectiveRate;
  }, [deriveUsdBalance, getEffectiveRate]);

  // Set up real-time listener using the Firebase Auth-confirmed UID.
  // This guarantees:
  // 1. Firebase Auth has fully initialized and has valid credentials
  // 2. The UID is the actual Firebase UID (not a stale localStorage value)
  // 3. Firestore security rules will accept the request
  useEffect(() => {
    if (!confirmedUid) {
      rawOlvBalanceRef.current = 0;
      setState({
        summary: createDefaultWalletSummary(),
        isLoading: false,
        error: null,
        summaryForUid: null,
      });
      return;
    }

    // Log if AuthContext user.id differs from the confirmed Firebase UID
    if (user?.id && user.id !== confirmedUid) {
      console.warn('[WalletSummaryContext] AuthContext UID differs — authContext:', user.id,
                   'firebase:', confirmedUid, '— using firebase UID');
    }

    setState((prev) => ({ ...prev, isLoading: true }));

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Start real-time sync using the confirmed Firebase UID
    console.log('[WalletSummaryContext] Starting real-time listener for user:', confirmedUid);
    unsubRef.current = onWalletSummarySnapshot(
      confirmedUid,
      (summary) => {
        // Store the raw OLV balance for rate change re-derivation
        rawOlvBalanceRef.current = summary.olvBalance;

        // Derive USD from OLV * current exchange rate (single source of truth)
        // Use refs to always get latest rate functions (avoids stale closure)
        const derivedUsdBalance = deriveUsdBalanceRef.current(summary.olvBalance);

        console.log('[WalletSummaryContext] Received summary update:', {
          olvBalance: summary.olvBalance,
          derivedUsdBalance,
          rate: getEffectiveRateRef.current(),
          totalOlvSpent: summary.totalOlvSpent,
          totalOlvEarned: summary.totalOlvEarned,
        });

        setState({
          summary: {
            ...summary,
            usdBalance: derivedUsdBalance, // Override with derived value
          },
          isLoading: false,
          error: null,
          summaryForUid: confirmedUid,
        });
      },
      (error) => {
        console.error('[WalletSummaryContext] Snapshot error:', error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error.message,
          summaryForUid: confirmedUid,
        }));
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedUid]);

  // ACCOUNT WALLET DISPLAY listener 1/2: acting-account back-pointer
  // (users/{uid}/account_membership/main → accountId). Mirrors the pattern in
  // useEntitlementBypass, which already gates the low-balance path on this
  // exact doc. Errors fail-safe to "no account" (personal display).
  useEffect(() => {
    if (!confirmedUid) {
      // Signed out: settled, no account context — resolver takes the
      // byte-for-byte-unchanged personal path.
      setAccountDisplay({ ...DEFAULT_ACCOUNT_DISPLAY_STATE, membershipLoaded: true });
      return;
    }
    // Reset on uid change so a stale account/mirror never leaks across users.
    setAccountDisplay(DEFAULT_ACCOUNT_DISPLAY_STATE);
    const unsub = onAccountMembershipAccountIdSnapshot(
      confirmedUid,
      (accountId) => {
        setAccountDisplay((prev) => {
          if (prev.membershipLoaded && prev.accountId === accountId) return prev;
          return {
            ...DEFAULT_ACCOUNT_DISPLAY_STATE,
            membershipLoaded: true,
            accountId,
            // A concrete account arms the mirror listener below.
            mirrorStatus: accountId ? 'loading' : 'idle',
          };
        });
      },
      () => {
        // Fail-safe: treat as no account context (personal display).
        setAccountDisplay({ ...DEFAULT_ACCOUNT_DISPLAY_STATE, membershipLoaded: true });
      }
    );
    return unsub;
  }, [confirmedUid]);

  // ACCOUNT WALLET DISPLAY listener 2/2: the server-written display mirror
  // (accounts/{accountId}/wallet/displaySummary). Permission errors (not a
  // verified member) and a missing doc (account never debited since the mirror
  // shipped — lazy backfill) both fall back to the personal wallet.
  const activeAccountId = accountDisplay.membershipLoaded ? accountDisplay.accountId : null;
  useEffect(() => {
    if (!confirmedUid || !activeAccountId) return;
    const unsub = onAccountWalletDisplaySnapshot(
      activeAccountId,
      (snap) => {
        setAccountDisplay((prev) => {
          if (prev.accountId !== activeAccountId) return prev; // stale listener
          return {
            ...prev,
            mirrorStatus: snap.exists ? 'ready' : 'missing',
            mirrorSummary: snap.summary,
            mirrorPayerUid: snap.payerUid,
            mirrorPlanAllocation: snap.planAllocation ?? null,
            mirrorBillingPause: snap.billingPause ?? null,
          };
        });
      },
      () => {
        setAccountDisplay((prev) => {
          if (prev.accountId !== activeAccountId) return prev;
          return {
            ...prev,
            mirrorStatus: 'error',
            mirrorSummary: null,
            mirrorPayerUid: null,
            mirrorPlanAllocation: null,
            mirrorBillingPause: null,
          };
        });
      }
    );
    return unsub;
  }, [confirmedUid, activeAccountId]);

  // Re-derive USD balance when exchange rate changes
  useEffect(() => {
    if (state.isLoading || rawOlvBalanceRef.current === 0) return;

    const newDerivedUsd = deriveUsdBalance(rawOlvBalanceRef.current);

    // Only update if the derived USD has meaningfully changed (avoid infinite loops)
    if (Math.abs(newDerivedUsd - state.summary.usdBalance) > 0.0001) {
      console.log('[WalletSummaryContext] Rate changed, re-deriving USD:', {
        olvBalance: rawOlvBalanceRef.current,
        oldUsd: state.summary.usdBalance,
        newUsd: newDerivedUsd,
        rate: getEffectiveRate(),
      });

      setState((prev) => ({
        ...prev,
        summary: {
          ...prev.summary,
          usdBalance: newDerivedUsd,
        },
      }));
    }
  }, [olvRateContext?.state.rateUsd, deriveUsdBalance, getEffectiveRate, state.isLoading, state.summary.usdBalance]);

  // Manual refresh — PERSONAL summary only. The account display mirror is
  // purely snapshot-driven (server writes it inside every debit transaction),
  // and the one caller of refresh() (post-withdraw on the wallet page) is a
  // personal-wallet money movement.
  const refresh = async () => {
    const uid = confirmedUid || user?.id;
    if (!uid) return;

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const summary = await getWalletSummary(uid);
      // Store raw OLV and derive USD
      rawOlvBalanceRef.current = summary.olvBalance;
      const derivedUsdBalance = deriveUsdBalance(summary.olvBalance);

      setState({
        summary: {
          ...summary,
          usdBalance: derivedUsdBalance, // Override with derived value
        },
        isLoading: false,
        error: null,
        summaryForUid: uid,
      });
    } catch (error) {
      console.error('[WalletSummaryContext] Refresh error:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: (error as Error).message,
      }));
    }
  };

  // FALSE-LOW-BALANCE FIX: the summary is only "loaded" when it belongs to
  // the CURRENT confirmed uid. During the one-commit sign-in window the
  // committed summary is still the signed-out default (summaryForUid null),
  // so consumers see isLoading:true and the balance gate stays silent —
  // useLowBalanceWarning already treats loading as non-blocking.
  const personalIsLoading =
    state.isLoading || (confirmedUid !== null && state.summaryForUid !== confirmedUid);

  // ACCOUNT WALLET DISPLAY (owner decision 2026-07-13: "display"): resolve
  // which wallet to DISPLAY — the acting account's paying wallet when the user
  // is in an account context and it isn't their own wallet; the personal wallet
  // (unchanged semantics, including the W-round summaryForUid loading gate)
  // everywhere else. Pure decision logic in walletDisplayResolver.ts.
  const mirrorInput: AccountMirrorInput<WalletSummary> = {
    status: accountDisplay.mirrorStatus,
    summary: accountDisplay.mirrorSummary,
    payerUid: accountDisplay.mirrorPayerUid,
  };
  const resolution = resolveDisplayedWallet<WalletSummary>({
    confirmedUid,
    personalSummary: state.summary,
    personalIsLoading,
    membershipLoaded: accountDisplay.membershipLoaded,
    membershipAccountId: accountDisplay.accountId,
    mirror: mirrorInput,
  });
  // Account-scope USD is derived inline from the mirror's OLV at the current
  // rate (the provider re-renders on every rate-context change, so this stays
  // live). The personal path keeps the existing ref-based derivation untouched.
  const displayedSummary: WalletSummary =
    resolution.scope === 'account'
      ? { ...resolution.summary, usdBalance: deriveUsdBalance(resolution.summary.olvBalance) }
      : state.summary;

  const value: WalletSummaryContextValue = {
    ...state,
    summary: displayedSummary,
    isLoading: resolution.isLoading,
    refresh,
    hasUser: confirmedUid !== null,
    displayScope: resolution.scope,
    displayAccountId: resolution.accountId,
    personalSummary: state.summary,
    personalIsLoading,
    planAllocation:
      accountDisplay.mirrorStatus === 'ready' && accountDisplay.accountId === activeAccountId
        ? accountDisplay.mirrorPlanAllocation
        : null,
    billingPause:
      accountDisplay.mirrorStatus === 'ready' && accountDisplay.accountId === activeAccountId
        ? accountDisplay.mirrorBillingPause
        : null,
  };

  return (
    <WalletSummaryContext.Provider value={value}>
      {children}
    </WalletSummaryContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access wallet summary (balance, totals)
 * Must be used within WalletSummaryProvider
 */
export function useWalletSummary(): WalletSummaryContextValue {
  const context = useContext(WalletSummaryContext);
  if (!context) {
    throw new Error('useWalletSummary must be used within a WalletSummaryProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if not within provider
 */
export function useMaybeWalletSummary(): WalletSummaryContextValue | null {
  return useContext(WalletSummaryContext);
}
